import api from './api';

export const MESSAGE_MAX_LENGTH = 4_000;
export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isMessageId = (value: unknown): value is string => typeof value === 'string' && UUID_PATTERN.test(value);

export interface MessageParticipant {
  userId: string;
  role: 'owner' | 'participant' | 'observer';
  lastReadAt: string | null;
  displayName?: string | null;
}
export interface LearnerThread {
  id: string;
  kind: 'instructor' | 'cohort_peer' | 'ai_tutor' | 'announcement';
  subject: string | null;
  createdAt: string;
  lastMessageAt: string | null;
  participants: MessageParticipant[];
  unreadCount: number;
}
export interface LearnerMessage {
  id: string;
  threadId: string;
  senderId: string | null;
  content: string;
  sentAt: string;
  attachments: Array<{ id: string; fileName: string }>;
}
export interface MessageCursor { before: string; beforeId: string }
export interface MessagePagination { limit: number; hasMore: boolean; nextCursor: MessageCursor | null }
export interface LearnerConversation { thread: LearnerThread; messages: LearnerMessage[]; pagination: MessagePagination }
export interface SupportThreadOrigin {
  threadId: string;
  escalationId: string;
  humanSessionId: string;
  sourceTeachingSessionId: string | null;
  sourceItemId: string | null;
}

export function mergeLearnerMessages(previous: LearnerMessage[], incoming: LearnerMessage[]): LearnerMessage[] {
  const rows = new Map(previous.map((message) => [message.id, message]));
  incoming.forEach((message) => rows.set(message.id, message));
  return Array.from(rows.values()).sort((a, b) => Date.parse(a.sentAt) - Date.parse(b.sentAt) || a.id.localeCompare(b.id));
}

const record = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('The message response could not be verified.');
  return value as Record<string, unknown>;
};
const timestamp = (value: unknown): value is string => typeof value === 'string' && Number.isFinite(Date.parse(value));
const nullableTimestamp = (value: unknown): value is string | null => value === null || timestamp(value);
const canonicalTimestamp = (value: unknown): value is string => timestamp(value) && new Date(value).toISOString() === value;

export function parseLearnerThread(value: unknown, viewerId: string): LearnerThread {
  const row = record(value);
  if (!isMessageId(row.id) || !['instructor', 'cohort_peer', 'ai_tutor', 'announcement'].includes(String(row.kind)) ||
      !(row.subject === null || typeof row.subject === 'string') || !timestamp(row.createdAt) ||
      !nullableTimestamp(row.lastMessageAt) || !Array.isArray(row.participants)) throw new Error('The conversation response could not be verified.');
  const participants = row.participants.map((value) => {
    const member = record(value);
    if (!isMessageId(member.userId) || !['owner', 'participant', 'observer'].includes(String(member.role)) ||
        !nullableTimestamp(member.lastReadAt) || !(member.displayName === undefined || member.displayName === null ||
          (typeof member.displayName === 'string' && member.displayName.length <= 200))) throw new Error('The conversation participants could not be verified.');
    return { userId: member.userId, role: member.role, lastReadAt: member.lastReadAt,
      ...(member.displayName === undefined ? {} : { displayName: member.displayName }) } as MessageParticipant;
  });
  if (!participants.some((member) => member.userId === viewerId)) throw new Error('This conversation is not available to your account.');
  const unreadCount = row.unreadCount ?? 0;
  if (typeof unreadCount !== 'number' || !Number.isSafeInteger(unreadCount) || unreadCount < 0) throw new Error('The unread count could not be verified.');
  return { id: row.id, kind: row.kind, subject: row.subject, createdAt: row.createdAt,
    lastMessageAt: row.lastMessageAt, participants, unreadCount } as LearnerThread;
}

export function parseLearnerMessage(value: unknown, threadId: string): LearnerMessage {
  const row = record(value);
  if (!isMessageId(row.id) || row.threadId !== threadId || !(row.senderId === null || isMessageId(row.senderId)) ||
      typeof row.content !== 'string' || !timestamp(row.sentAt) || !Array.isArray(row.attachments)) {
    throw new Error('The saved message could not be verified.');
  }
  const attachments = row.attachments.map((value) => {
    const item = record(value);
    if (!isMessageId(item.id) || typeof item.fileName !== 'string') throw new Error('The attachment response could not be verified.');
    // Storage URLs are deliberately not exposed as arbitrary clickable links.
    return { id: item.id, fileName: item.fileName };
  });
  return { id: row.id, threadId, senderId: row.senderId, content: row.content, sentAt: row.sentAt, attachments } as LearnerMessage;
}

/** Notifications supply an identity, never an arbitrary destination URL. */
export function messageNotificationHref(kind: string, payload: Record<string, unknown>): string | null {
  return kind === 'instructor_message' && isMessageId(payload.thread_id)
    ? `/portal/messages?threadId=${payload.thread_id}` : null;
}

export function createMessageRequestId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (!globalThis.crypto?.getRandomValues) throw new Error('Secure message retries are unavailable in this browser.');
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const learnerMessagesApi = {
  async origin(threadId: string, token: string): Promise<SupportThreadOrigin | null> {
    if (!isMessageId(threadId)) throw new Error('Choose a valid conversation.');
    const response = await api.get(`/support/threads/${threadId}/context`, { headers: { Authorization: `Bearer ${token}` } });
    if (response.data?.context === null) return null;
    const context = record(response.data?.context);
    if (context.threadId !== threadId || !isMessageId(context.escalationId) || !isMessageId(context.humanSessionId) ||
        !(context.sourceTeachingSessionId === null || (typeof context.sourceTeachingSessionId === 'string' && context.sourceTeachingSessionId.length <= 512)) ||
        !(context.sourceItemId === null || isMessageId(context.sourceItemId))) throw new Error('The follow-up context could not be verified.');
    return { threadId, escalationId: context.escalationId, humanSessionId: context.humanSessionId,
      sourceTeachingSessionId: context.sourceTeachingSessionId, sourceItemId: context.sourceItemId } as SupportThreadOrigin;
  },
  async list(viewerId: string, token: string): Promise<LearnerThread[]> {
    const response = await api.get('/messages/threads', { params: { limit: 100 }, headers: { Authorization: `Bearer ${token}` } });
    if (!Array.isArray(response.data?.threads)) throw new Error('The inbox response could not be verified.');
    return response.data.threads.map((row: unknown) => parseLearnerThread(row, viewerId))
      .filter((thread: LearnerThread) => thread.kind !== 'ai_tutor');
  },
  async get(threadId: string, viewerId: string, token: string, cursor?: MessageCursor): Promise<LearnerConversation> {
    if (!isMessageId(threadId)) throw new Error('Choose a valid conversation.');
    if (cursor && (!canonicalTimestamp(cursor.before) || !isMessageId(cursor.beforeId))) throw new Error('The message cursor could not be verified.');
    const response = await api.get(`/messages/threads/${threadId}`, { headers: { Authorization: `Bearer ${token}` },
      params: { limit: 50, ...(cursor ? { before: cursor.before, before_id: cursor.beforeId } : {}) } });
    const thread = parseLearnerThread(response.data?.thread, viewerId);
    if (thread.id !== threadId || thread.kind === 'ai_tutor' || !Array.isArray(response.data?.messages)) throw new Error('This conversation response is unavailable.');
    const messages = response.data.messages.map((row: unknown) => parseLearnerMessage(row, threadId));
    if (messages.some((message: LearnerMessage) => message.senderId !== null && !thread.participants.some((member) => member.userId === message.senderId))) throw new Error('A message sender could not be verified against this conversation.');
    const page = record(response.data?.pagination);
    const boundary = page.nextCursor === null ? null : record(page.nextCursor);
    if (typeof page.limit !== 'number' || !Number.isInteger(page.limit) || page.limit < 1 || page.limit > 100 ||
        typeof page.hasMore !== 'boolean' || (page.hasMore && (!boundary || !canonicalTimestamp(boundary.before) || !isMessageId(boundary.beforeId))) ||
        (!page.hasMore && boundary !== null)) throw new Error('The message history cursor could not be verified.');
    return { thread, messages: mergeLearnerMessages([], messages), pagination: page as unknown as MessagePagination };
  },
  async send(threadId: string, content: string, requestId: string, viewerId: string, token: string): Promise<LearnerMessage> {
    if (!isMessageId(threadId) || !isMessageId(requestId) || !content.trim() || content.length > MESSAGE_MAX_LENGTH) throw new Error('Enter a message of 1–4,000 characters.');
    const response = await api.post(`/messages/threads/${threadId}/messages`, { content: content.trim(), client_message_id: requestId }, { headers: { Authorization: `Bearer ${token}` } });
    const message = parseLearnerMessage(response.data?.message, threadId);
    if (message.senderId !== viewerId) throw new Error('The saved message sender could not be verified.');
    return message;
  },
  async markRead(threadId: string, token: string): Promise<void> {
    if (!isMessageId(threadId)) throw new Error('Choose a valid conversation.');
    const response = await api.patch(`/messages/threads/${threadId}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    if (response.data?.ok !== true) throw new Error('Read status could not be confirmed.');
  },
};
